
import { Component, Vue } from "vue-property-decorator";
import Sidebar from "@/components/navigations/Sidebar.vue";
import CardActivite from "@/components/card/CardActivite.vue";
import CardActiviteOpe from "@/components/card/CardActiviteOpe.vue";
import Cta from "@/components/buttons/Button.vue";
import Alert from "@/components/alert/Alert.vue";
import Tag from "@/components/tags/Tag.vue";
import Tabs from "@/components/tabs/Tab.vue";
import { login, timeline } from "@/store";
import { Activite, User } from "@/models";
import { isSameDay } from "@/helpers";
import dayjs from "dayjs";

@Component({
  components: {
    Sidebar,
    Cta,
    Alert,
    CardActivite,
    CardActiviteOpe,
    Tag,
    Tabs,
  },
  methods: {
    isSameDay,
  },
})
/** Admin activity management — tabbed list (ongoing/upcoming/past/programmed/removed) + Google import flow. */
export default class OpeActivites extends Vue {
  private dataReady = false;

  private openEope(): void {
    window.open(
      "https://eope.protectioncivile.org",
      "_blank",
      "noopener,noreferrer"
    );
  }
  private canContinueImport = false;
  private importInProgress = false;
  private canStartImport = true;

  private tabSelected = "current";
  private tabs = [
    { key: "current", label: "En cours" },
    { key: "soon", label: "A venir" },
    { key: "past", label: "Passées" },
    { key: "program", label: "Programmées" },
    { key: "removed", label: "Annulées" },
  ];

  async mounted(): Promise<void> {
    await timeline.activitesList();
    this.dataReady = true;

    if (this.activitesEnCours && this.activitesEnCours.length) {
      this.tabSelected = "current";
    } else {
      this.tabSelected = "soon";
    }
  }

  private date(date: string, format = "DD/MM/YYYY"): string {
    return dayjs(date).format(format);
  }

  private createActivite(): void {
    this.$router.push("/activites/creer");
  }

  private goToActivite(id: string) {
    this.$router.push(`/activite/${id}`);
  }

  /**
   * Opens the external Google OAuth flow in a new tab, then enables the "continue" button after
   * a short delay (2s — comment below previously said 5s, doesn't match the code) to give the
   * user time to complete auth before importing.
   */
  private launchImportProcess(): void {
    // Ouvrir une nouvelle fenêtre pour l'authentification
    window.open(
      `https://connect.protectioncivile-paris8.org/external/googleapis-ope`,
      "_blank"
    );
    this.canStartImport = false;
    // Attendre 5 secondes pour vérifier si l'import est terminé
    setTimeout(() => {
      this.canContinueImport = true;
    }, 2000);
  }

  /** Triggers the actual bulk import (assumes the Google OAuth step from {@link launchImportProcess} is done), then refreshes the list. */
  private async continueImportProcess(): Promise<void> {
    this.importInProgress = true;
    await timeline.importActivites();
    this.importInProgress = false;
    this.canContinueImport = false;
    this.canStartImport = true;

    this.dataReady = false;
    await timeline.activitesList();
    this.dataReady = true;
  }

  /** Activities currently in their time window, closed for sign-up, with a team already assigned. */
  get activitesEnCours(): Activite[] | null {
    if (this.activites) {
      return this.activites.filter(
        (a) =>
          dayjs().isAfter(a.debut) &&
          dayjs().isBefore(a.fin) &&
          a.etat?.id === "CLOSE" &&
          a.equipe != null &&
          a.equipe.length > 0
      );
    } else {
      return null;
    }
  }

  /** Upcoming, non-removed, non-imported/programmed activities, soonest first. */
  get activitesAVenir(): Activite[] | null {
    if (this.activites) {
      return this.activites
        .filter(
          (a) =>
            dayjs().isBefore(a.debut) &&
            a.etat?.id !== "IMPORT" &&
            a.etat?.id !== "PROGRAM" &&
            a.do?.id !== "REMOVE"
        )
        .sort((a, b) => dayjs(a.debut).diff(dayjs(b.debut)));
    } else {
      return null;
    }
  }

  /** Past, non-removed, non-imported/programmed activities. */
  get activitesPasse(): Activite[] | null {
    if (this.activites) {
      return this.activites.filter(
        (a) =>
          dayjs().isAfter(a.fin) &&
          a.etat?.id !== "IMPORT" &&
          a.etat?.id !== "PROGRAM" &&
          a.do?.id !== "REMOVE"
      );
    } else {
      return null;
    }
  }

  /** Removed/cancelled activities ("do.id === REMOVE"), most recent first. */
  get activitesRetirees(): Activite[] | null {
    if (this.activites) {
      return this.activites
        .filter((a) => a.do?.id === "REMOVE")
        .sort((a, b) => dayjs(b.debut).diff(dayjs(a.debut)));
    } else {
      return null;
    }
  }

  /** Upcoming activities in "IMPORT" state (just pulled in via the Google import flow), soonest first. */
  get activitesImported(): Activite[] | null {
    if (this.activites) {
      return this.activites
        .filter((a) => dayjs().isBefore(a.debut) && a.etat?.id === "IMPORT")
        .sort((a, b) => dayjs(a.debut).diff(dayjs(b.debut)));
    } else {
      return null;
    }
  }

  /** Upcoming activities in "PROGRAM" state (scheduled but not yet opened for sign-up), soonest first. */
  get activitesProgramed(): Activite[] | null {
    if (this.activites) {
      return this.activites
        .filter((a) => dayjs().isBefore(a.debut) && a.etat?.id === "PROGRAM")
        .sort((a, b) => dayjs(a.debut).diff(dayjs(b.debut)));
    } else {
      return null;
    }
  }

  get activites(): Activite[] | null {
    return timeline.getActivitesList;
  }

  get context(): User | null {
    return login.getContext;
  }
}
